import React from 'react';
import { motion } from 'framer-motion';

/**
 * LiveTranslatorAnimation
 * ------------------------------------------------------------------
 * A single soundwave enters from the left, splits into two coloured
 * strands (pink + blue — both already used across the site), carries
 * floating fragments of different scripts (Turkish, Arabic, Chinese,
 * Japanese, French, Spanish) that dissolve into English as they
 * cross the centre, and merges back into one clean waveform on the
 * right.
 *
 * All colours are drawn from the existing GoogLyn palette:
 *   - #E8A0B8  brand pink
 *   - #4A7C9D  muted teal (already used in TicketOpsSolutions etc.)
 *   - #E8E4DC / #E8EDE7  page backgrounds
 * No new colours or fonts are introduced.
 */

const COLOR_A = '#E8A0B8'; // pink strand
const COLOR_B = '#4A7C9D'; // teal strand
const INK_SOFT = 'rgba(0, 0, 0, 0.35)';

// Path geometry — viewBox 400 x 260, centreY = 130
const PATH_SINGLE_LEFT = 'M 0 130 C 15 115 30 145 45 130 S 75 115 90 130';
const PATH_STRAND_A = 'M 90 130 Q 145 60 200 130 T 310 130';
const PATH_STRAND_B = 'M 90 130 Q 145 200 200 130 T 310 130';
const PATH_SINGLE_RIGHT = 'M 310 130 C 325 115 340 145 355 130 S 385 115 400 130';

// Floating word fragments — foreign script → English
const WORDS_A = [
  { fg: 'Merhaba',   en: 'Hello' },
  { fg: '你好',      en: 'Hello' },
  { fg: 'こんにちは', en: 'Hello' },
  { fg: 'Olá',       en: 'Hello' },
  { fg: '안녕하세요',  en: 'Hello' },
];
const WORDS_B = [
  { fg: 'مرحبا',   en: 'Hello' },
  { fg: 'Bonjour', en: 'Hello' },
  { fg: 'Hola',    en: 'Hello' },
  { fg: 'नमस्ते',   en: 'Hello' },
];

const FloatingWord = ({ fg, en, color, yStart, yPeak, delay, cycle }) => {
  // The word travels along its strand: x: 90 → 200 → 310
  // fg script visible on the first half, dissolves into English at centre.
  return (
    <g style={{ pointerEvents: 'none' }}>
      {/* Foreign script — fades in early, fades out at centre */}
      <motion.text
        fontSize="15"
        fontWeight="500"
        textAnchor="middle"
        fill={color}
        initial={{ opacity: 0 }}
        animate={{
          x: [90, 155, 200],
          y: [yStart, yPeak, yStart],
          opacity: [0, 1, 0],
        }}
        transition={{
          duration: cycle * 0.55,
          delay,
          repeat: Infinity,
          repeatDelay: cycle * 0.45,
          ease: 'easeInOut',
          times: [0, 0.55, 1],
        }}
      >
        {fg}
      </motion.text>

      {/* English — fades in at centre, fades out at strand end */}
      <motion.text
        fontSize="15"
        fontWeight="500"
        textAnchor="middle"
        fill="rgba(0,0,0,0.8)"
        initial={{ opacity: 0 }}
        animate={{
          x: [200, 255, 310],
          y: [yStart, yPeak, yStart],
          opacity: [0, 1, 0],
        }}
        transition={{
          duration: cycle * 0.55,
          delay: delay + cycle * 0.35,
          repeat: Infinity,
          repeatDelay: cycle * 0.45,
          ease: 'easeInOut',
          times: [0, 0.55, 1],
        }}
      >
        {en}
      </motion.text>
    </g>
  );
};

const LiveTranslatorAnimation = () => {
  const cycle = 5; // seconds per full pass
  return (
    <div
      className="relative w-full mx-auto"
      style={{ maxWidth: '30rem' }}
      aria-label="Live translator waveform animation"
    >
      <svg
        viewBox="0 0 400 260"
        className="w-full h-auto"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Marker glow at the split & merge points */}
        <defs>
          <radialGradient id="pulseA" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={COLOR_A} stopOpacity="0.55" />
            <stop offset="100%" stopColor={COLOR_A} stopOpacity="0" />
          </radialGradient>
          <radialGradient id="pulseB" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={COLOR_B} stopOpacity="0.55" />
            <stop offset="100%" stopColor={COLOR_B} stopOpacity="0" />
          </radialGradient>
          <path id="strandA-path" d={PATH_STRAND_A} />
          <path id="strandB-path" d={PATH_STRAND_B} />
        </defs>

        {/* Input single wave (left) */}
        <path
          d={PATH_SINGLE_LEFT}
          stroke={INK_SOFT}
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
        />

        {/* Two divergent strands */}
        <motion.path
          d={PATH_STRAND_A}
          stroke={COLOR_A}
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 1.4, ease: 'easeOut' }}
        />
        <motion.path
          d={PATH_STRAND_B}
          stroke={COLOR_B}
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 1.4, delay: 0.15, ease: 'easeOut' }}
        />

        {/* Merged single wave (right) */}
        <motion.path
          d={PATH_SINGLE_RIGHT}
          stroke="rgba(0, 0, 0, 0.6)"
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.6, ease: 'easeOut' }}
        />

        {/* Split-point glow */}
        <motion.circle
          cx="90"
          cy="130"
          r="10"
          fill="url(#pulseA)"
          animate={{ scale: [1, 1.6, 1], opacity: [0.9, 0.3, 0.9] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
          style={{ transformOrigin: '90px 130px' }}
        />
        {/* Merge-point glow */}
        <motion.circle
          cx="310"
          cy="130"
          r="10"
          fill="url(#pulseB)"
          animate={{ scale: [1, 1.6, 1], opacity: [0.9, 0.3, 0.9] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }}
          style={{ transformOrigin: '310px 130px' }}
        />

        {/* Travelling pulse dots along each strand */}
        {[0, 1.2, 2.4].map((begin, i) => (
          <React.Fragment key={`pulse-${i}`}>
            <circle r="3.5" fill={COLOR_A}>
              <animateMotion
                dur="3.6s"
                repeatCount="indefinite"
                begin={`${begin}s`}
                rotate="auto"
              >
                <mpath xlinkHref="#strandA-path" />
              </animateMotion>
            </circle>
            <circle r="3.5" fill={COLOR_B}>
              <animateMotion
                dur="3.6s"
                repeatCount="indefinite"
                begin={`${begin + 0.6}s`}
                rotate="auto"
              >
                <mpath xlinkHref="#strandB-path" />
              </animateMotion>
            </circle>
          </React.Fragment>
        ))}

        {/* Floating text fragments — top strand (pink) */}
        {WORDS_A.map((w, idx) => (
          <FloatingWord
            key={`a-${idx}`}
            fg={w.fg}
            en={w.en}
            color={COLOR_A}
            yStart={110}
            yPeak={62}
            delay={idx * (cycle / WORDS_A.length)}
            cycle={cycle}
          />
        ))}

        {/* Floating text fragments — bottom strand (teal) */}
        {WORDS_B.map((w, idx) => (
          <FloatingWord
            key={`b-${idx}`}
            fg={w.fg}
            en={w.en}
            color={COLOR_B}
            yStart={150}
            yPeak={200}
            delay={idx * (cycle / WORDS_B.length) + cycle / (WORDS_B.length * 2)}
            cycle={cycle}
          />
        ))}
      </svg>
    </div>
  );
};

export default LiveTranslatorAnimation;
